import type { SelectedReport, WorkerState } from "./roster.interfaces";
import { DaySheetStatus, JobTypes } from "./roster.types";

export {
  DaySheetType,
  JobTypes,
  MenuActions,
  ProjectType,
  ReportTypes,
  ShiftType,
  StatusTypes,
} from "./roster.types";

export const ContractJobs = [
  JobTypes.DAY_CONTRACT_JOB,
  JobTypes.NIGHT_CONTRACT_JOB,
];

export const LabourJobs = [JobTypes.DAY_LABOUR_JOB, JobTypes.NIGHT_LABOUR_JOB];

export const ROSTER_SESSION_KEYS = {
  ROSTER_SELECTED_DATE: "rosterSelectedDate",
};

export const MAX_SITE_ADDRESS_LENGTH = 200;

export const PDR_VISIBLE_STATUSES = [DaySheetStatus.CLOSED];

export const ROSTER_STICKY_COLUMN_CLASSES = {
  selectorHeader:
    "w-8 min-w-8 max-w-8 px-1.5 sticky left-0 z-10 sticky-shadow-left",
  selectorCell:
    "w-8 min-w-8 max-w-8 px-1.5 z-[9] sticky left-0 bg-white dark:bg-[#172032] sticky-shadow-left",
  jobOverviewHeader:
    "min-w-[150px] sticky right-[105px] dark:bg-[#0F2056] z-[4] sticky-shadow-right",
  jobOverviewCell:
    "min-w-[150px] z-[4] sticky right-[105px] bg-white dark:bg-[#172032] sticky-shadow-right",
  jobActionsHeader: "sticky right-[0px] dark:bg-[#0F2056] z-[9]",
  jobActionsCell:
    "min-w-[120px] z-[9] sticky right-[0px] bg-white dark:bg-[#172032]",
  fleetSelectorHeader:
    "w-8 min-w-8 max-w-8 px-1.5 sticky left-0 z-10 sticky-shadow-left",
  fleetSelectorCell:
    "w-8 min-w-8 max-w-8 px-1.5 z-[9] sticky left-0 bg-white dark:bg-[#172032] sticky-shadow-left",
  fleetTaskStatusHeader:
    "min-w-[120px] sticky-shadow-right sticky right-[150px] dark:bg-[#0F2056] z-[4]",
  fleetTaskStatusCell:
    "min-w-[120px] sticky-shadow-right z-[4] sticky right-[150px] bg-white dark:bg-[#172032]",
  fleetActionsHeader: "sticky right-[0px] dark:bg-[#0F2056] z-[9]",
  fleetActionsCell:
    "min-w-[150px] z-[9] sticky right-[0px] bg-white dark:bg-[#172032]",
} as const;

export const ROSTER_PDF_LABELS = {
  reportType: "Report Type",
  dailyReportNo: "Daily Report No.",
  date: "Date",
  workerName: "Worker Name",
  customer: "Customer",
  address: "Address",
  projectName: "Project Name",
  projectNo: "Project No",
  projectAddress: "Project Address",
  jobReferenceNo: "Job Reference No",
  siteManager: "Site Manager",
  preparedBy: "Prepared By",
  status: "Status",
  statusSubmitted: "Submitted",
  startTime: "Start Time",
  finishTime: "Finish Time",
  nextDayPlan: "Next Day Plan",
  qtyLabelSuffix: "(QTY)",
  projectTypeDocket: "Docket",
  projectTypeContract: "Contract",
} as const;

export const rosterApiRoutes = {
  daySheet: "workspace/:workspace/roster/day-sheet",
  daySheetAssignedEmployees:
    "workspace/:workspace/roster/day-sheet/assigned-employees",
  daySheetDrivers: "workspace/:workspace/roster/day-sheet/drivers",
  activeProjectSupervisors:
    "workspace/:workspace/roster/day-sheet/project/:project/active-supervisors",
  calendarDaySheet: "workspace/:workspace/roster/calendar",
  singleDaySheet: "workspace/:workspace/roster/day-sheet/:daySheetId",
  daySheetEmployee:
    "workspace/:workspace/roster/day-sheet/:daySheetId/employee/:employeeId",
  daySheetSubContractorEmployee:
    "workspace/:workspace/roster/day-sheet/:daySheetId/sub-contractor/:employeeId",
  daySheetQuestions:
    "workspace/:workspace/roster/day-sheet/:daySheetId/question/:reportType",
  daySheetActivity:
    "workspace/:workspace/roster/day-sheet/:daySheetId/activity",
  fleetActivity: "workspace/:workspace/roster/fleets/:fleetId/activity",
  daySheetTasks: "workspace/:workspace/roster/day-sheet/tasks/:projectId",
  daySheetWaste: "workspace/:workspace/roster/day-sheet/:daySheetId/waste",
  daySheetEmployeeCollection:
    "workspace/:workspace/roster/day-sheet/:daySheetId/employee",
  daySheetFleet: "workspace/:workspace/roster/day-sheet/:daySheetId/fleet",
  daySheetFloatingAction: "workspace/:workspace/roster/day-sheet/:action",
  fleets: "workspace/:workspace/roster/fleets",
  fleetAction: "workspace/:workspace/roster/fleets/:action",
  allocatedFleets: "workspace/:workspace/roster/fleets/allocated",
  allocatedFleetAction: "workspace/:workspace/roster/fleets/allocated/:action",
  transferAllocatedFleet:
    "workspace/:workspace/roster/fleets/allocated/:allocatedFleetId/unallocate/:fleetBookingId",
  allocatedFleetStatus:
    "workspace/:workspace/roster/fleets/allocated/:allocatedFleetId/status",
  allocatedFleetById:
    "workspace/:workspace/roster/fleets/allocated/:allocatedFleetId",
  reportSubmissionStatus:
    "workspace/:workspace/roster/report/:reportSubmissionId/status",
  forceCloseRosterDetails:
    "workspace/:workspace/roster/day-sheet/:daySheetId/close-check",
  rosterReportMandatory:
    "workspace/:workspace/roster/day-sheet/:daySheetId/report/:reportSubmissionId/mandatory",
  rosterReportSubmit:
    "workspace/:workspace/roster/day-sheet/:daySheetId/report/:reportSubmissionId/submit",
  forceCloseRoster:
    "workspace/:workspace/roster/day-sheet/:daySheetId/force-close",
  fleetReportRemoveMandatory:
    "workspace/:workspace/roster/fleets/:fleetId/report/:reportId/remove-mandatory",
  fleetReportSubmit:
    "workspace/:workspace/roster/fleets/:fleetId/report/:reportId/submit",
  forceCloseFleetDetails:
    "workspace/:workspace/roster/fleets/allocated/:fleetId/close-check",
  forceCloseFleet:
    "workspace/:workspace/roster/fleets/allocated/:fleetId/force-close",
  reorderFleetBookings: "workspace/:workspace/roster/fleets/reorder",
} as const;

export const INITIAL_REPORT_STATE: SelectedReport = {
  type: null,
  id: null,
  isOpen: false,
} as const;

export const INITIAL_WORKER_STATE: WorkerState = {
  selectedId: "",
  isOpen: false,
  type: null,
  employee: null,
  subcontractor: null,
};
